import { useApi } from 'api/api-context';
import {
  DataMultiSelect,
  DataMultiSelectInheritedProps,
  DataSelect,
  DataSelectInheritedProps,
} from 'components/selects/DataSelect';
import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';

/**
 * Fetches organizations form API.
 */
function useOrganizationSelect({
  showDiscarded,
  permissionSlug,
}: {
  showDiscarded?: boolean;
  permissionSlug?: string;
}) {
  const { getAction } = useApi();

  const fetchOrganizations = useCallback(async () => {
    const listOrganizations = getAction('OrganizationList');
    const getCurrentUserInfo = getAction('AuthLoggedUserInfo');

    const [organizations, user] = await Promise.all([
      listOrganizations({
        query: {
          filters: {
            'showDiscarded.eq': showDiscarded ? 1 : 0,
            'permissionSlug.eq': permissionSlug,
          },
        },
      }),
      getCurrentUserInfo(),
    ]);

    return organizations
      .sort((a, b) => {
        // First show my organization
        // Then all active sort alphabetically
        // Then at the end show discarded organizations sort alphabetically

        if (a.organizationId === user.organizationId) return -1;
        if (b.organizationId === user.organizationId) return 1;

        if (a.status !== b.status) {
          return a.status ? -1 : 1;
        }

        return a.organizationName.localeCompare(b.organizationName);
      })
      .map((organization) => ({
        label: (organization.status ? '' : '[VYRADENÁ] ') + organization.organizationName,
        value: organization.organizationId,
        group: user.organizationId === organization.organizationId ? 'Moja organizácia' : 'Zákaznícke organizácie',
      }));
  }, [getAction, showDiscarded, permissionSlug]);

  return { fetchOrganizations };
}

/**
 * The shared props of the select components.
 */
interface OrganizationSelectProp {
  showDiscarded?: boolean;
  permissionSlug?: string;
}

/**
 * The type of the Organization Select component.
 */
type OrganizationSelectProps = OrganizationSelectProp & DataSelectInheritedProps;

/**
 * The type of the Organization Multiselect component.
 */
type OrganizationMultiSelectProps = OrganizationSelectProp & DataMultiSelectInheritedProps;

/**
 * Select input for organizations.
 */
export function OrganizationSelect({
  showDiscarded = false,
  permissionSlug = undefined,
  ...props
}: OrganizationSelectProps) {
  const key = useMemo(() => nanoid(), [showDiscarded, permissionSlug]);
  const { fetchOrganizations } = useOrganizationSelect({ showDiscarded, permissionSlug });
  return (
    <DataSelect
      key={key}
      action={fetchOrganizations}
      labelProp="label"
      valueProp="value"
      groupProp="group"
      {...props}
    />
  );
}

/**
 * Select input for organizations (multiple).
 */
export function OrganizationMultiSelect({
  showDiscarded = false,
  permissionSlug = undefined,
  ...props
}: OrganizationMultiSelectProps) {
  const key = useMemo(() => nanoid(), [showDiscarded, permissionSlug]);

  const { fetchOrganizations } = useOrganizationSelect({ showDiscarded, permissionSlug });
  return (
    <DataMultiSelect
      key={key}
      action={fetchOrganizations}
      labelProp="label"
      valueProp="value"
      groupProp="group"
      {...props}
    />
  );
}
